import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BE_API_AUTH_URL } from '../../constants/app.constants';
import {
  AdminSignOrderSettingsConfiguration,
  AdminSignProductConfiguration,
  PricingTier,
  ShippingTier,
} from '../../models/sign-order.models';

const MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER;

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function isSafeIntegerAtLeast(value: unknown, minimum: number): value is number {
  return typeof value === 'number'
    && Number.isSafeInteger(value)
    && value >= minimum
    && value <= MAX_SAFE_INTEGER;
}

function positiveSafeIntegerValidator(control: AbstractControl): ValidationErrors | null {
  if (isBlank(control.value)) {
    return null;
  }
  return isSafeIntegerAtLeast(control.value, 1) ? null : { positiveWholeNumber: true };
}

function requiredNonNegativeCentsValidator(control: AbstractControl): ValidationErrors | null {
  if (isBlank(control.value)) {
    return { required: true };
  }
  return isSafeIntegerAtLeast(control.value, 0) ? null : { nonNegativeWholeNumber: true };
}

function requiredPositiveQuantityValidator(control: AbstractControl): ValidationErrors | null {
  if (isBlank(control.value)) {
    return { required: true };
  }
  return isSafeIntegerAtLeast(control.value, 1) ? null : { positiveWholeNumber: true };
}

function finiteMaximumValidator(control: AbstractControl): ValidationErrors | null {
  if (control.parent?.get('unlimited')?.value === true) {
    return null;
  }
  if (isBlank(control.value)) {
    return { required: true };
  }
  return isSafeIntegerAtLeast(control.value, 1) ? null : { positiveWholeNumber: true };
}

function tierArrayValidator(priceControlNames: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormArray) || control.length === 0) {
      return null;
    }

    let previousMaximum = 0;
    for (let index = 0; index < control.length; index += 1) {
      const tier = control.at(index) as FormGroup;
      const minimum = tier.get('minQuantity')?.value;
      const unlimited = tier.get('unlimited')?.value === true;
      const maximum = unlimited ? null : tier.get('maxQuantity')?.value;

      if (!isSafeIntegerAtLeast(minimum, 1)) {
        return { tierQuantityInvalid: true };
      }
      if (index === 0 && minimum !== 1) {
        return { tierStartsAtOne: true };
      }
      if (index > 0 && minimum !== previousMaximum + 1) {
        return { tierRangeGapOrOverlap: true };
      }
      if (!unlimited && !isSafeIntegerAtLeast(maximum, minimum)) {
        return { tierMaximumInvalid: true };
      }
      if (unlimited && index !== control.length - 1) {
        return { tierUnlimitedNotFinal: true };
      }
      if (index === control.length - 1 && maximum !== null) {
        return { tierFinalMustBeUnlimited: true };
      }
      if (maximum !== null) {
        previousMaximum = maximum;
      }

      for (const priceControlName of priceControlNames) {
        if (!isSafeIntegerAtLeast(tier.get(priceControlName)?.value, 0)) {
          return { tierPriceInvalid: true };
        }
      }
    }

    return null;
  };
}

function activeProductValidator(control: AbstractControl): ValidationErrors | null {
  if (control.get('active')?.value !== true) {
    return null;
  }

  const pricingTiers = control.get('pricingTiers');
  if (!(pricingTiers instanceof FormArray) || pricingTiers.length === 0) {
    return { activeProductPricingTiersRequired: true };
  }
  if (isBlank(control.get('unitWeightGrams')?.value)) {
    return { activeProductWeightRequired: true };
  }
  return null;
}

function enabledOrderingValidator(control: AbstractControl): ValidationErrors | null {
  if (control.get('orderingEnabled')?.value !== true) {
    return null;
  }

  const shippingTiers = control.get('shippingTiers');
  return shippingTiers instanceof FormArray && shippingTiers.length > 0
    ? null
    : { enabledOrderingShippingTiersRequired: true };
}

interface ProductUpdatePayload {
  active: boolean;
  name: { de: string; en: string };
  description: { de: string; en: string };
  minimumCopiesPerProperty: number;
  maximumCopiesPerProperty: number;
  maximumTotalQuantity: number | null;
  allowedDrillingOptions: Array<'without_drilling' | 'with_drilling'>;
  pricingTiers: PricingTier[];
  unitWeightGrams: number | null;
  _rev?: number;
}

interface SettingsUpdatePayload {
  orderingEnabled: boolean;
  vatRateBasisPoints: number;
  shippingTiers: ShippingTier[];
  paymentRecipient: string;
  iban: string;
  bic: string;
  paymentDueDays: number;
  paymentInstructions: { de: string; en: string };
  adminNotificationRecipients: string[];
  _rev?: number;
}

@Component({
  selector: 'app-admin-sign-order-settings',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, FormsModule,
    NzAlertComponent, NzButtonModule, NzCardModule, NzFormModule,
    NzIconModule, NzInputModule, NzInputNumberModule, NzModalModule, NzSwitchModule,
    NzTabsModule, NzTableModule, NzToolTipModule, NzUploadModule, NzSpinModule,
    TranslateModule,
  ],
  templateUrl: './sign-order-settings.component.html',
  styleUrls: ['./sign-order-settings.component.less'],
})
export class AdminSignOrderSettingsComponent implements OnInit, OnDestroy {
  private blobUrls: Record<number, string> = {};
  private loadingUrls = new Set<number>();
  private previewGeneration = 0;

  previewUrl: string | null = null;
  previewVisible = false;
  settingsForm!: FormGroup;
  productForm!: FormGroup;
  settingsLoading = true;
  productLoading = true;
  savingSettings = false;
  savingProduct = false;
  settingsRev: number | null = null;
  productRev: number | null = null;
  settingsError: string | null = null;
  productError: string | null = null;
  product: AdminSignProductConfiguration | null = null;
  uploadUrl = '';

  constructor(
    private readonly fb: FormBuilder,
    private readonly http: HttpClient,
    private readonly message: NzMessageService,
    public readonly translate: TranslateService,
  ) {}

  get authToken(): string {
    return `Bearer ${localStorage.getItem('authToken') || ''}`;
  }

  get pricingTiers(): FormArray {
    return this.productForm.get('pricingTiers') as FormArray;
  }

  get shippingTiers(): FormArray {
    return this.settingsForm.get('shippingTiers') as FormArray;
  }

  ngOnInit(): void {
    this.uploadUrl = `${BE_API_AUTH_URL}/admin/sign-orders/product/A5-ALU-DIBOND/images`;
    this.buildSettingsForm();
    this.buildProductForm();
    this.loadSettings();
    this.loadProduct();
  }

  private buildSettingsForm(): void {
    this.settingsForm = this.fb.group({
      orderingEnabled: [false],
      vatRateBasisPoints: [1900, [Validators.required, requiredNonNegativeCentsValidator, Validators.max(10000)]],
      shippingTiers: this.fb.array([], { validators: tierArrayValidator(['shippingNetCents']) }),
      paymentRecipient: ['', Validators.maxLength(160)],
      iban: ['', Validators.maxLength(64)],
      bic: ['', Validators.maxLength(32)],
      paymentDueDays: [14, [Validators.required, requiredNonNegativeCentsValidator, Validators.max(365)]],
      paymentInstructionsDe: ['', Validators.maxLength(5000)],
      paymentInstructionsEn: ['', Validators.maxLength(5000)],
      adminNotificationRecipients: ['', Validators.maxLength(2000)],
    }, { validators: enabledOrderingValidator });
  }

  private buildProductForm(): void {
    this.productForm = this.fb.group({
      active: [false],
      nameDe: ['', Validators.maxLength(5000)],
      nameEn: ['', Validators.maxLength(5000)],
      descriptionDe: ['', Validators.maxLength(5000)],
      descriptionEn: ['', Validators.maxLength(5000)],
      minimumCopiesPerProperty: [1, [Validators.required, requiredPositiveQuantityValidator]],
      maximumCopiesPerProperty: [99, [Validators.required, requiredPositiveQuantityValidator]],
      maximumTotalQuantity: [null, positiveSafeIntegerValidator],
      unitWeightGrams: [null, positiveSafeIntegerValidator],
      allowWithoutDrilling: [true],
      allowWithDrilling: [true],
      pricingTiers: this.fb.array([], { validators: tierArrayValidator(['unitNetCents', 'drillingUnitNetCents']) }),
    }, { validators: activeProductValidator });
  }

  loadSettings(): void {
    this.settingsLoading = true;
    this.settingsError = null;
    this.http.get<AdminSignOrderSettingsConfiguration>(`${BE_API_AUTH_URL}/admin/sign-orders/settings`).subscribe({
      next: (settings) => {
        this.applySettingsResponse(settings);
        this.settingsLoading = false;
      },
      error: () => {
        this.settingsError = this.translate.instant('SIGN_ORDER.ADMIN.CONFIG_ERROR');
        this.settingsLoading = false;
      },
    });
  }

  loadProduct(): void {
    this.productLoading = true;
    this.productError = null;
    this.http.get<AdminSignProductConfiguration>(`${BE_API_AUTH_URL}/admin/sign-orders/product/A5-ALU-DIBOND`).subscribe({
      next: (product) => {
        this.applyProductResponse(product);
        this.productLoading = false;
      },
      error: () => {
        this.productError = this.translate.instant('SIGN_ORDER.ADMIN.CONFIG_ERROR');
        this.productLoading = false;
      },
    });
  }

  addSignPricingTier(): void {
    if (!this.canAddTier(this.pricingTiers)) {
      return;
    }
    this.pricingTiers.push(this.createSignPricingTier({
      minQuantity: this.nextMinimum(this.pricingTiers),
      maxQuantity: null,
      unitNetCents: 0,
      drillingUnitNetCents: 0,
    }));
    this.pricingTiers.markAsTouched();
    this.pricingTiers.updateValueAndValidity();
  }

  removeSignPricingTier(index: number): void {
    this.pricingTiers.removeAt(index);
    this.pricingTiers.markAsTouched();
    this.pricingTiers.updateValueAndValidity();
  }

  addShippingTier(): void {
    if (!this.canAddTier(this.shippingTiers)) {
      return;
    }
    this.shippingTiers.push(this.createShippingTier({
      minQuantity: this.nextMinimum(this.shippingTiers),
      maxQuantity: null,
      shippingNetCents: 0,
    }));
    this.shippingTiers.markAsTouched();
    this.shippingTiers.updateValueAndValidity();
  }

  removeShippingTier(index: number): void {
    this.shippingTiers.removeAt(index);
    this.shippingTiers.markAsTouched();
    this.shippingTiers.updateValueAndValidity();
  }

  setTierUnlimited(tier: AbstractControl, checkedChange: boolean | Event): void {
    const unlimited = typeof checkedChange === 'boolean'
      ? checkedChange
      : tier.get('unlimited')?.value === true;
    if (unlimited) {
      tier.get('maxQuantity')?.setValue(null);
    }
    const maximum = tier.get('maxQuantity');
    maximum?.markAsTouched();
    maximum?.updateValueAndValidity();
    tier.updateValueAndValidity();
    tier.parent?.updateValueAndValidity();
  }

  canAddSignPricingTier(): boolean {
    return this.canAddTier(this.pricingTiers);
  }

  canAddShippingTier(): boolean {
    return this.canAddTier(this.shippingTiers);
  }

  saveSettings(): void {
    if (this.savingSettings) {
      return;
    }
    this.settingsForm.markAllAsTouched();
    this.settingsForm.updateValueAndValidity();
    if (this.settingsForm.invalid) {
      return;
    }

    this.savingSettings = true;
    this.settingsError = null;
    const value = this.settingsForm.getRawValue();
    const body: SettingsUpdatePayload = {
      orderingEnabled: value.orderingEnabled === true,
      vatRateBasisPoints: Number(value.vatRateBasisPoints),
      shippingTiers: this.serializeShippingTiers(),
      paymentRecipient: String(value.paymentRecipient || ''),
      iban: String(value.iban || ''),
      bic: String(value.bic || ''),
      paymentDueDays: Number(value.paymentDueDays),
      paymentInstructions: {
        de: String(value.paymentInstructionsDe || ''),
        en: String(value.paymentInstructionsEn || ''),
      },
      adminNotificationRecipients: String(value.adminNotificationRecipients || '')
        .split(',')
        .map((entry: string) => entry.trim())
        .filter(Boolean),
      _rev: this.settingsRev ?? undefined,
    };

    this.http.put<AdminSignOrderSettingsConfiguration>(`${BE_API_AUTH_URL}/admin/sign-orders/settings`, body).subscribe({
      next: (settings) => {
        this.savingSettings = false;
        this.applySettingsResponse(settings);
        this.message.success(this.translate.instant('SIGN_ORDER.ADMIN.CONFIG_SAVED'));
      },
      error: (requestError: unknown) => {
        this.savingSettings = false;
        this.settingsError = this.getRequestErrorMessage(requestError, 'SIGN_ORDER.ADMIN.CONFIG_SAVE_ERROR');
        this.message.error(this.settingsError);
      },
    });
  }

  saveProduct(): void {
    if (this.savingProduct) {
      return;
    }
    this.productForm.markAllAsTouched();
    this.productForm.updateValueAndValidity();
    if (this.productForm.invalid) {
      return;
    }

    this.savingProduct = true;
    this.productError = null;
    const value = this.productForm.getRawValue();
    const drillingOptions: Array<'without_drilling' | 'with_drilling'> = [];
    if (value.allowWithoutDrilling === true) {
      drillingOptions.push('without_drilling');
    }
    if (value.allowWithDrilling === true) {
      drillingOptions.push('with_drilling');
    }

    const body: ProductUpdatePayload = {
      active: value.active === true,
      name: { de: String(value.nameDe || ''), en: String(value.nameEn || '') },
      description: { de: String(value.descriptionDe || ''), en: String(value.descriptionEn || '') },
      minimumCopiesPerProperty: Number(value.minimumCopiesPerProperty),
      maximumCopiesPerProperty: Number(value.maximumCopiesPerProperty),
      maximumTotalQuantity: isBlank(value.maximumTotalQuantity) ? null : Number(value.maximumTotalQuantity),
      unitWeightGrams: isBlank(value.unitWeightGrams) ? null : Number(value.unitWeightGrams),
      allowedDrillingOptions: drillingOptions,
      pricingTiers: this.serializeSignPricingTiers(),
      _rev: this.productRev ?? undefined,
    };

    this.http.put<AdminSignProductConfiguration>(`${BE_API_AUTH_URL}/admin/sign-orders/product/A5-ALU-DIBOND`, body).subscribe({
      next: (product) => {
        this.savingProduct = false;
        this.applyProductResponse(product);
        this.message.success(this.translate.instant('SIGN_ORDER.ADMIN.CONFIG_SAVED'));
      },
      error: (requestError: unknown) => {
        this.savingProduct = false;
        this.productError = this.getRequestErrorMessage(requestError, 'SIGN_ORDER.ADMIN.CONFIG_SAVE_ERROR');
        this.message.error(this.productError);
      },
    });
  }

  deleteImage(index: number): void {
    this.http.delete(`${BE_API_AUTH_URL}/admin/sign-orders/product/A5-ALU-DIBOND/images/${index}`).subscribe({
      next: () => this.loadProduct(),
      error: (requestError: unknown) => this.message.error(this.getRequestErrorMessage(requestError, 'SIGN_ORDER.ADMIN.CONFIG_SAVE_ERROR')),
    });
  }

  onImageUpload(info: { type?: string }): void {
    if (info.type === 'success') {
      this.loadProduct();
    }
  }

  moveImageUp(index: number): void {
    if (index <= 0 || !this.product) {
      return;
    }
    const order = this.product.images.map((_, imageIndex) => imageIndex);
    [order[index], order[index - 1]] = [order[index - 1], order[index]];
    this.reorderImages(order);
  }

  moveImageDown(index: number): void {
    if (!this.product || index >= this.product.images.length - 1) {
      return;
    }
    const order = this.product.images.map((_, imageIndex) => imageIndex);
    [order[index], order[index + 1]] = [order[index + 1], order[index]];
    this.reorderImages(order);
  }

  getImagePreviewUrl(index: number): string {
    if (!this.product?.images[index]) {
      return '';
    }
    if (this.blobUrls[index]) {
      return this.blobUrls[index];
    }
    if (this.loadingUrls.has(index)) {
      return '';
    }

    this.loadingUrls.add(index);
    const generation = this.previewGeneration;
    const url = `${BE_API_AUTH_URL}/admin/sign-orders/product/${encodeURIComponent(this.product.code)}/images/${index}`;
    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        if (generation !== this.previewGeneration) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        this.blobUrls[index] = blobUrl;
        this.loadingUrls.delete(index);
      },
      error: () => {
        if (generation === this.previewGeneration) {
          this.loadingUrls.delete(index);
        }
      },
    });
    return '';
  }

  getImageTooltipTitle(assetKey: string, index: number): string {
    return assetKey.split('/').pop() || `Image ${index + 1}`;
  }

  openPreview(url: string): void {
    this.previewUrl = url;
    this.previewVisible = true;
  }

  closePreview(): void {
    this.previewVisible = false;
    this.previewUrl = null;
  }

  saveImageMeta(index: number): void {
    if (!this.product) {
      return;
    }
    const image = this.product.images[index];
    this.http.put(`${BE_API_AUTH_URL}/admin/sign-orders/product/A5-ALU-DIBOND/images/${index}`, {
      captionDe: image.caption.de || '',
      captionEn: image.caption.en || '',
      altDe: image.alt.de || '',
      altEn: image.alt.en || '',
    }).subscribe({
      next: () => {
        this.message.success(this.translate.instant('SIGN_ORDER.ADMIN.CONFIG_SAVED'));
        this.loadProduct();
      },
      error: (requestError: unknown) => this.message.error(this.getRequestErrorMessage(requestError, 'SIGN_ORDER.ADMIN.CONFIG_SAVE_ERROR')),
    });
  }

  reorderImages(order: number[]): void {
    this.http.put(`${BE_API_AUTH_URL}/admin/sign-orders/product/A5-ALU-DIBOND/images/reorder`, { order }).subscribe({
      next: () => this.loadProduct(),
      error: (requestError: unknown) => this.message.error(this.getRequestErrorMessage(requestError, 'SIGN_ORDER.ADMIN.CONFIG_SAVE_ERROR')),
    });
  }

  productTierValidationKey(): string | null {
    return this.tierValidationKey(this.pricingTiers.errors);
  }

  shippingTierValidationKey(): string | null {
    return this.tierValidationKey(this.shippingTiers.errors);
  }

  productFormValidationKey(): string | null {
    if (this.productForm.errors?.['activeProductWeightRequired']) {
      return 'SIGN_ORDER.ADMIN.WEIGHT_REQUIRED_FOR_ACTIVE_PRODUCT';
    }
    if (this.productForm.errors?.['activeProductPricingTiersRequired']) {
      return 'SIGN_ORDER.ADMIN.PRICING_TIERS_REQUIRED_FOR_ACTIVE_PRODUCT';
    }
    return null;
  }

  unitWeightValidationKey(): string | null {
    const weight = this.productForm.get('unitWeightGrams');
    return weight?.touched && weight.invalid && !isBlank(weight.value)
      ? 'SIGN_ORDER.ADMIN.WEIGHT_INVALID'
      : null;
  }

  settingsFormValidationKey(): string | null {
    return this.settingsForm.errors?.['enabledOrderingShippingTiersRequired']
      ? 'SIGN_ORDER.ADMIN.SHIPPING_TIERS_REQUIRED_FOR_ORDERING'
      : null;
  }

  ngOnDestroy(): void {
    this.resetProductImagePreviews();
  }

  private applySettingsResponse(settings: AdminSignOrderSettingsConfiguration): void {
    this.settingsRev = settings.__v;
    this.settingsForm.patchValue({
      orderingEnabled: settings.orderingEnabled,
      vatRateBasisPoints: settings.vatRateBasisPoints,
      paymentRecipient: settings.paymentRecipient,
      iban: settings.iban,
      bic: settings.bic,
      paymentDueDays: settings.paymentDueDays,
      paymentInstructionsDe: settings.paymentInstructions?.de || '',
      paymentInstructionsEn: settings.paymentInstructions?.en || '',
      adminNotificationRecipients: (settings.adminNotificationRecipients || []).join(', '),
    });
    this.shippingTiers.clear();
    (settings.shippingTiers || []).forEach((tier) => this.shippingTiers.push(this.createShippingTier(tier)));
    this.settingsForm.markAsPristine();
    this.settingsForm.markAsUntouched();
  }

  private applyProductResponse(product: AdminSignProductConfiguration): void {
    this.resetProductImagePreviews();
    this.product = product;
    this.productRev = product.__v;
    this.productForm.patchValue({
      active: product.active,
      nameDe: product.name?.de || '',
      nameEn: product.name?.en || '',
      descriptionDe: product.description?.de || '',
      descriptionEn: product.description?.en || '',
      minimumCopiesPerProperty: product.minimumCopiesPerProperty,
      maximumCopiesPerProperty: product.maximumCopiesPerProperty,
      maximumTotalQuantity: product.maximumTotalQuantity,
      unitWeightGrams: product.unitWeightGrams ?? null,
      allowWithoutDrilling: (product.allowedDrillingOptions || []).includes('without_drilling'),
      allowWithDrilling: (product.allowedDrillingOptions || []).includes('with_drilling'),
    });
    this.pricingTiers.clear();
    (product.pricingTiers || []).forEach((tier) => this.pricingTiers.push(this.createSignPricingTier(tier)));
    this.productForm.markAsPristine();
    this.productForm.markAsUntouched();
  }

  private resetProductImagePreviews(): void {
    this.previewGeneration++;
    for (const url of Object.values(this.blobUrls)) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls = {};
    this.loadingUrls.clear();
    this.previewUrl = null;
    this.previewVisible = false;
  }

  private createSignPricingTier(tier: PricingTier): FormGroup {
    const group = this.fb.group({
      minQuantity: [tier.minQuantity, requiredPositiveQuantityValidator],
      maxQuantity: [tier.maxQuantity, finiteMaximumValidator],
      unlimited: [tier.maxQuantity === null],
      unitNetCents: [tier.unitNetCents, requiredNonNegativeCentsValidator],
      drillingUnitNetCents: [tier.drillingUnitNetCents, requiredNonNegativeCentsValidator],
    });
    group.get('maxQuantity')?.updateValueAndValidity({ emitEvent: false });
    return group;
  }

  private createShippingTier(tier: ShippingTier): FormGroup {
    const group = this.fb.group({
      minQuantity: [tier.minQuantity, requiredPositiveQuantityValidator],
      maxQuantity: [tier.maxQuantity, finiteMaximumValidator],
      unlimited: [tier.maxQuantity === null],
      shippingNetCents: [tier.shippingNetCents, requiredNonNegativeCentsValidator],
    });
    group.get('maxQuantity')?.updateValueAndValidity({ emitEvent: false });
    return group;
  }

  private serializeSignPricingTiers(): PricingTier[] {
    return this.pricingTiers.controls.map((tier) => ({
      minQuantity: Number(tier.get('minQuantity')?.value),
      maxQuantity: this.serializeTierMaximum(tier),
      unitNetCents: Number(tier.get('unitNetCents')?.value),
      drillingUnitNetCents: Number(tier.get('drillingUnitNetCents')?.value),
    }));
  }

  private serializeShippingTiers(): ShippingTier[] {
    return this.shippingTiers.controls.map((tier) => ({
      minQuantity: Number(tier.get('minQuantity')?.value),
      maxQuantity: this.serializeTierMaximum(tier),
      shippingNetCents: Number(tier.get('shippingNetCents')?.value),
    }));
  }

  private canAddTier(tiers: FormArray): boolean {
    if (tiers.length === 0) {
      return true;
    }
    const lastTier = tiers.at(tiers.length - 1);
    const maximum = lastTier.get('maxQuantity')?.value;
    return lastTier.get('unlimited')?.value !== true
      && isSafeIntegerAtLeast(maximum, 1)
      && maximum < MAX_SAFE_INTEGER;
  }

  private serializeTierMaximum(tier: AbstractControl): number | null {
    if (tier.get('unlimited')?.value === true) {
      return null;
    }

    const maximum = tier.get('maxQuantity')?.value;
    if (!isSafeIntegerAtLeast(maximum, 1)) {
      throw new Error('A finite tier maximum must be a positive safe integer');
    }
    return maximum;
  }

  private nextMinimum(tiers: FormArray): number {
    if (tiers.length === 0) {
      return 1;
    }
    const maximum = tiers.at(tiers.length - 1).get('maxQuantity')?.value;
    return isSafeIntegerAtLeast(maximum, 1) && maximum < MAX_SAFE_INTEGER ? maximum + 1 : 1;
  }

  private tierValidationKey(errors: ValidationErrors | null): string | null {
    if (!errors) {
      return null;
    }
    if (errors['tierStartsAtOne']) {
      return 'SIGN_ORDER.ADMIN.TIER_VALIDATION_STARTS_AT_ONE';
    }
    if (errors['tierRangeGapOrOverlap']) {
      return 'SIGN_ORDER.ADMIN.TIER_VALIDATION_GAPS_OR_OVERLAPS';
    }
    if (errors['tierUnlimitedNotFinal'] || errors['tierFinalMustBeUnlimited']) {
      return 'SIGN_ORDER.ADMIN.TIER_VALIDATION_FINAL_UNLIMITED';
    }
    if (errors['tierMaximumInvalid']) {
      return 'SIGN_ORDER.ADMIN.TIER_VALIDATION_MAXIMUM';
    }
    return 'SIGN_ORDER.ADMIN.TIER_VALIDATION_VALUES';
  }

  private getRequestErrorMessage(requestError: unknown, fallbackKey: string): string {
    if (typeof requestError === 'object' && requestError !== null) {
      const response = requestError as { error?: { message?: unknown } };
      if (typeof response.error?.message === 'string' && response.error.message.trim()) {
        return response.error.message;
      }
    }
    return this.translate.instant(fallbackKey);
  }
}
